import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { formatMoney } from '@hex/calculators-shared/utils';
import { track } from '@hex/shared/tracking';
import { Button, Flex, tokens, Typography, View } from '@sapphire-react/core';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MINIMUM_LOAN_VALUE } from '../../../constants';
import backgroundImage from '../../images/background.png';
import { calculateLoanLowerBound } from '../../utils/calculateLoanLowerBound';

type Props = {
  loanAmount?: number;
  isDisabled?: boolean;
};

export const Header: React.FC<Props> = ({ loanAmount = 0, isDisabled = false }) => {
  const { t } = useTranslation();
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const abTestVersion = window.abTestVersion ?? 'a';
  const lowerBound = calculateLoanLowerBound(loanAmount);

  return (
    <BackgroundWrapper>
      <View
        width={'100%'}
        borderTopLeftRadius={tokens.size.radiusMd}
        borderTopRightRadius={tokens.size.radiusMd}
        overflow={'hidden'}
        paddingTop={tokens.size.spacingXl.value}
        paddingBottom={tokens.size.spacingXl.value}
      >
        <Typography.Body
          textAlign={'center'}
          marginBottom={tokens.size.spacingXs}
          size={'sm'}
          UNSAFE_style={{ color: tokens.color.foregroundOnNegative.value }}
        >
          {t('results.preliminaryBorrow.heading')}
        </Typography.Body>
        <Typography.Heading
          textAlign={'center'}
          marginBottom={tokens.size.spacingXs}
          level={2}
          data-test-id={'loan-amount-result'}
          UNSAFE_style={{
            color: tokens.color.foregroundOnNegative.value,
            fontSize: isExtraSmall || isSmall ? tokens.size.fontHeading2xl.value : tokens.size.fontHeadingLg.value,
          }}
        >
          {abTestVersion === 'b' && loanAmount > MINIMUM_LOAN_VALUE
            ? `${formatMoney(lowerBound, { currency: '€' })} - ${formatMoney(loanAmount || 0, { currency: '€' })}`
            : formatMoney(loanAmount || 0, { currency: '€' })}
        </Typography.Heading>
        <Typography.Body
          textAlign={'center'}
          marginTop={tokens.size.spacingMd}
          size={'sm'}
          UNSAFE_style={{
            color: tokens.color.foregroundOnNegative.value,
            paddingLeft: tokens.size.spacingXl.value,
            paddingRight: tokens.size.spacingXl.value,
          }}
        >
          {t('results.preliminaryBorrow.subHeading')}
        </Typography.Body>
        <Flex flexFlow={'row wrap'} justifyContent={'center'}>
          <Button
            marginTop={tokens.size.spacingLg}
            data-test-id={'loan-application-redirect'}
            data-tracking-id={'Housing:Mortgage:Loan application'}
            isDisabled={isDisabled}
            minHeight={44}
            onPress={() => {
              track('Housing:Mortgage:Loan application');
              window.open(t('results.preliminaryBorrow.link'), '_blank', 'noopener,noreferrer');
            }}
          >
            {t('button.applyForLoan')}
          </Button>
        </Flex>
      </View>
    </BackgroundWrapper>
  );
};

const BackgroundWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  border-top-left-radius: ${tokens.size.radiusSm.value};
  border-top-right-radius: ${tokens.size.radiusSm.value};
`;
